import React from 'react';
import ReactDOM from 'react-dom';
import { devClickDebug } from '../../devClickDebug';
import {
  DashboardPageHeader,
  dashboardCardClass,
  dashboardInputClass,
  dashboardSelectClass,
  dashboardBtnPrimary,
  dashboardLabelClass,
  dashboardBtnRowEdit,
  dashboardBtnRowDelete,
  dashboardBtnRowAccent,
  dashboardBtnModalCancel,
  dashboardBtnModalDanger,
  dashboardModalBackdrop,
  dashboardModalWrap,
  dashboardModalPanel,
  dashboardModalPanelMd,
  dashboardModalHeader,
  dashboardModalBody,
  dashboardModalFooter,
  dashboardModalCloseBtn,
} from './DashboardChrome';

const ClientsSection = ({
  clients,
  filteredClients,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  showAddClient,
  setShowAddClient,
  clientForm,
  setClientForm,
  saveClient,
  deleteClient,
  editClient,
  handleClientFormChange,
  formatPhoneNumber,
  quotes,
  setSelectedClientForQuotes,
  setShowClientQuotes,
  // Add any additional props needed for modals, etc.
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [clientToDelete, setClientToDelete] = React.useState(null);

  // Pagination logic
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClients = filteredClients.slice(startIndex, endIndex);

  // Reset to first page when search/filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Delete confirmation handlers
  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (clientToDelete) {
      deleteClient(clientToDelete.id);
      setShowDeleteModal(false);
      setClientToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setClientToDelete(null);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-10">
      <DashboardPageHeader
        eyebrow="Dashboard"
        title="Clients"
        description="Keep contact details and status in one place — tied to quotes and invoices."
        action={
          <button
            type="button"
            onClick={() => {
              devClickDebug('ClientsSection: Add new client button click', {});
              setShowAddClient(true);
            }}
            className={dashboardBtnPrimary}
          >
            Add new client
          </button>
        }
      />

      <div className={dashboardCardClass}>
        <div className="flex flex-col items-start justify-between gap-4 border-b border-slate-100 bg-slate-50/50 px-6 py-4 md:flex-row md:items-center">
          <h3 className="text-base font-semibold text-slate-900">Client database</h3>
          <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
            <input
              type="text"
              placeholder="Search by name or project type…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={dashboardInputClass}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`${dashboardSelectClass} min-w-[140px]`}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="p-6">
          {filteredClients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{clients.length === 0 ? 'No clients yet' : 'No clients match your search'}</p>
              <p className="text-sm">
                {clients.length === 0 ? 'Add your first client to get started' : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {currentClients.map((client) => {
                return (
                  <div
                    key={client.id}
                    className="flex flex-col gap-3 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm transition-all hover:border-slate-300 hover:shadow-md sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h4 className="text-lg font-semibold text-gray-900">{client.name}</h4>
                        <div className={`w-3 h-3 rounded-full shrink-0 ${client.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      </div>
                      <div className="text-sm text-gray-600 mb-2 space-y-0.5">
                        {client.email && (
                          <div className="flex items-start gap-2 min-w-0">
                            <span className="font-medium shrink-0">Email:</span>
                            <span className="text-gray-900 break-all">{client.email}</span>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium shrink-0">Phone:</span>
                            <span className="text-gray-900">{client.phone}</span>
                          </div>
                        )}
                        {client.street && (
                          <div className="flex items-start gap-2">
                            <span className="font-medium shrink-0">Address:</span>
                            <span className="text-gray-900">
                              {client.street}, {client.city}, {client.province} {client.postalCode}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm flex-wrap">
                        {/* Status Dropdown */}
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-600 dark:text-slate-400">Status:</span>
                          <select
                            value={client.status}
                            onChange={e => {
                              const updatedClient = { ...client, status: e.target.value };
                              saveClient(updatedClient);
                            }}
                            className={`${dashboardSelectClass} min-w-[7.5rem] cursor-pointer py-1.5 font-semibold ${
                              client.status === 'active'
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/55 dark:text-emerald-200'
                                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/45 dark:text-red-200'
                            }`}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:items-end sm:gap-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setClientForm(client);
                            setShowAddClient(true);
                          }}
                          className={dashboardBtnRowEdit}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          Edit
                        </button>
                        <button type="button" onClick={() => handleDeleteClick(client)} className={dashboardBtnRowDelete}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const clientQuotes = quotes.filter((quote) => quote.clientId === client.id);
                            setSelectedClientForQuotes({ ...client, quotes: clientQuotes });
                            setShowClientQuotes(true);
                          }}
                          className={dashboardBtnRowAccent}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Invoices ({quotes.filter((quote) => quote.clientId === client.id).length})
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredClients.length)} of {filteredClients.length} clients
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-primary text-white shadow-md shadow-primary/20'
                              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal &&
        ReactDOM.createPortal(
          <>
            <div className={dashboardModalBackdrop} aria-hidden />
            <div className={dashboardModalWrap}>
              <div className={dashboardModalPanelMd} role="dialog" aria-modal="true" aria-labelledby="delete-client-title">
                <div className={dashboardModalHeader}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-100">
                      <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Clients</p>
                      <h3 id="delete-client-title" className="text-lg font-semibold text-slate-900">
                        Delete client
                      </h3>
                    </div>
                  </div>
                </div>
                <div className={dashboardModalBody}>
                  <p className="text-sm text-slate-600">
                    Are you sure you want to delete{' '}
                    <span className="font-semibold text-slate-900">{clientToDelete?.name}</span>?
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">
                    This cannot be undone. Associated invoice links for this client may need to be reviewed separately.
                  </p>
                </div>
                <div className={dashboardModalFooter}>
                  <button type="button" onClick={handleCancelDelete} className={dashboardBtnModalCancel}>
                    Cancel
                  </button>
                  <button type="button" onClick={handleConfirmDelete} className={dashboardBtnModalDanger}>
                    Delete client
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
      
      {/* Add/Edit Client Modal */}
      {showAddClient &&
        ReactDOM.createPortal(
          <>
            <div className={dashboardModalBackdrop} aria-hidden />
            <div className={dashboardModalWrap}>
              <div className={dashboardModalPanel} role="dialog" aria-modal="true" aria-labelledby="client-form-title">
                <div className={dashboardModalHeader}>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Clients</p>
                    <h3 id="client-form-title" className="text-lg font-semibold text-slate-900">
                      {clientForm.id ? 'Edit client' : 'New client'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddClient(false);
                      setClientForm({});
                    }}
                    className={dashboardModalCloseBtn}
                    title="Close"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    devClickDebug('ClientsSection: client form onSubmit', {
                      hasName: !!clientForm?.name,
                      isEdit: !!clientForm?.id,
                    });
                    saveClient(clientForm);
                  }}
                  className="flex flex-1 flex-col overflow-hidden"
                >
                  <div className={`${dashboardModalBody} space-y-4`}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={dashboardLabelClass}>Name *</label>
                        <input
                          type="text"
                          required
                          value={clientForm.name || ''}
                          onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>Email</label>
                        <input
                          type="email"
                          value={clientForm.email || ''}
                          onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>Phone</label>
                        <input
                          type="tel"
                          value={clientForm.phone || ''}
                          onChange={(e) => setClientForm({ ...clientForm, phone: formatPhoneNumber(e.target.value) })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>Street address</label>
                        <input
                          type="text"
                          value={clientForm.street || ''}
                          onChange={(e) => setClientForm({ ...clientForm, street: e.target.value })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>City</label>
                        <input
                          type="text"
                          value={clientForm.city || ''}
                          onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>Province</label>
                        <input
                          type="text"
                          value={clientForm.province || ''}
                          onChange={(e) => setClientForm({ ...clientForm, province: e.target.value })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>Postal code</label>
                        <input
                          type="text"
                          value={clientForm.postalCode || ''}
                          onChange={(e) => setClientForm({ ...clientForm, postalCode: e.target.value })}
                          className={dashboardInputClass}
                        />
                      </div>
                      <div>
                        <label className={dashboardLabelClass}>Status</label>
                        <select
                          value={clientForm.status || 'active'}
                          onChange={(e) => setClientForm({ ...clientForm, status: e.target.value })}
                          className={dashboardSelectClass}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className={dashboardModalFooter}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddClient(false);
                        setClientForm({});
                      }}
                      className={dashboardBtnModalCancel}
                    >
                      Cancel
                    </button>
                    <button type="submit" className={dashboardBtnPrimary}>
                      {clientForm.id ? 'Save changes' : 'Add client'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>,
          document.body
        )}
      {/* Modals and other UI can be added here using ...modalsAndOtherProps */}
    </div>
  );
};

export default ClientsSection; 